import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./styles/Purchase.css";

function Purchase() {
  const [purchases, setPurchases] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPurchaseDetail, setSelectedPurchaseDetail] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("today");
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("detailed");
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filter options
  const [categories, setCategories] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  
  // Product states
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const productInputRef = useRef(null);
  const productDropdownRef = useRef(null);
  
  // Vendor states
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const vendorInputRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [showVendorList, setShowVendorList] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingVendor, setEditingVendor] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  
  // Product form
  const [productForm, setProductForm] = useState({
    category: "",
    item_name: "",
    base_unit: "PCS",
    purchase_packaging: "",
    packaging_quantity: 1,
    vendor_id: "",
    vendor_name: ""
  });
  
  // Vendor form
  const [vendorForm, setVendorForm] = useState({
    name: "",
    phone: "",
    payment_methods: "",
    delivery_type: "",
    id_number: "",
    category: ""
  });
  
  // Purchase form
  const [formData, setFormData] = useState({
    category: "",
    product_id: "",
    product_name: "",
    vendor_id: "",
    vendor_name: "",
    quantity: "",
    unit: "PCS",
    price_per_unit: "",
    total_amount: ""
  });

  const getToken = () => localStorage.getItem("token") || "";
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  const isAdmin = staff.role === "Admin";

  const vendorCategories = [
    "PACKAGING", "FRUITS AND VEGGY", "BUTCHERY", "GROCERIES",
    "MILK AND CHEESE", "SPICES", "SOFT DRINKS", "COFFEE",
    "STATIONERY", "BEERS AND LIQUORS", "DRINKS", "EGGS",
    "SERVICE AND REPAIR", "KITCHENWARE", "SALES AND MARKETING"
  ];

  const productCategories = [
    "PACKS", "FRUITS AND VEGETABLES", "GROCERY", "MEAT AND CHICKEN",
    "MILK", "SAUCE", "SPICES", "SOFT DRINKS", "BEERS",
    "LIQUORS", "OTHER", "CLEANING", "IT AND INTERNET", 
    "KITCHENWARE", "STATIONARY", "SERVICE AND REPAIR", 
    "SALES AND MARKETING", "SALARY", "STAFF", "TRANSPORT", "DELIVERY FEE"
  ];

  const safeNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const formatTZS = (value) => {
    const num = safeNumber(value);
    return Math.round(num).toLocaleString();
  };

  // Export purchases report
  const exportPurchasesReport = async () => {
    setIsExporting(true);
    try {
      let filteredData = [...filteredPurchases];
      
      if (filteredData.length === 0) {
        setMessage({ text: "No purchases found for the selected period", type: "error" });
        setIsExporting(false);
        setShowReportModal(false);
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
        return;
      }
      
      let csvContent = "";
      let fileName = "";
      
      if (exportFormat === "summary") {
        const productSummary = {};
        
        filteredData.forEach(item => {
          const key = item.product_name;
          if (!productSummary[key]) {
            productSummary[key] = {
              product_name: item.product_name,
              category: item.category,
              total_quantity: 0,
              total_amount: 0,
              avg_price: 0,
              purchase_count: 0
            };
          }
          productSummary[key].total_quantity += safeNumber(item.quantity);
          productSummary[key].total_amount += safeNumber(item.total_amount);
          productSummary[key].purchase_count++;
        });
        
        Object.keys(productSummary).forEach(key => {
          productSummary[key].avg_price = productSummary[key].total_amount / productSummary[key].total_quantity;
        });
        
        const summaryArray = Object.values(productSummary);
        summaryArray.sort((a, b) => b.total_amount - a.total_amount);
        
        const headers = [
          "S/N", "Product Name", "Category", "Total Quantity Purchased",
          "Unit", "Total Amount Spent (TZS)", "Average Price per Unit (TZS)", "Number of Purchases"
        ];
        
        const rows = summaryArray.map((item, index) => [
          index + 1,
          item.product_name,
          item.category || '-',
          item.total_quantity.toFixed(2),
          filteredData.find(p => p.product_name === item.product_name)?.unit || 'PCS',
          item.total_amount.toFixed(2),
          item.avg_price.toFixed(2),
          item.purchase_count
        ]);
        
        const totalAmount = summaryArray.reduce((sum, item) => sum + item.total_amount, 0);
        
        csvContent = headers.join(",") + "\n";
        csvContent += rows.map(row => 
          row.map(cell => {
            if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(",")
        ).join("\n");
        
        csvContent += "\n\n\"GRAND TOTAL:\",,,,,,\"" + formatTZS(totalAmount) + "\"";
        fileName = `purchases_summary_${new Date().toISOString().split('T')[0]}`;
        
      } else {
        const headers = [
          "S/N", "Date", "Product Name", "Category", "Vendor",
          "Quantity", "Unit", "Price per Unit (TZS)", "Total Amount (TZS)"
        ];
        
        const rows = filteredData.map((item, index) => [
          index + 1,
          new Date(item.date).toLocaleDateString('en-GB'),
          item.product_name || '-',
          item.category || '-',
          item.vendor_name || '-',
          item.quantity,
          item.unit || 'PCS',
          item.price_per_unit,
          item.total_amount
        ]);
        
        const totalAmount = filteredData.reduce((sum, item) => sum + (item.total_amount || 0), 0);
        
        csvContent = headers.join(",") + "\n";
        csvContent += rows.map(row => 
          row.map(cell => {
            if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(",")
        ).join("\n");
        
        csvContent += "\n\n\"TOTAL:\",,,,,,,,\"" + formatTZS(totalAmount) + "\"";
        fileName = `purchases_detailed_${new Date().toISOString().split('T')[0]}`;
      }
      
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setMessage({ text: `Report exported successfully!`, type: "success" });
      setShowReportModal(false);
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } catch (error) {
      console.error("Export error:", error);
      setMessage({ text: "Failed to export report", type: "error" });
      setTimeout(() => setMessage({ text: "", type: "" }), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      const token = getToken();
      let url = `https://salathai.co.tz/mrms/Products.php?token=${token}&products=1&all=1`;
      const response = await axios.get(url);
      
      let productsData = [];
      if (response.data && Array.isArray(response.data)) {
        productsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        productsData = response.data.data;
      } else {
        productsData = [];
      }
      
      setProducts(productsData);
      setProductList(productsData);
      setFilteredProducts(productsData);
      setProductsList([...new Set(productsData.map(p => p.item_name))]);
      
      return productsData;
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setProductList([]);
      setFilteredProducts([]);
      return [];
    }
  };

  // Fetch vendors
  const fetchVendorsData = async () => {
    try {
      const token = getToken();
      let url = `https://salathai.co.tz/mrms/Vendor.php?token=${token}&vendors=1`;
      const response = await axios.get(url);
      
      let vendorsData = [];
      if (Array.isArray(response.data)) {
        vendorsData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        vendorsData = response.data.data;
      } else {
        vendorsData = [];
      }
      
      setVendors(vendorsData);
      setVendorList(vendorsData);
      setFilteredVendors(vendorsData);
      setVendorsList([...new Set(vendorsData.map(v => v.name))]);
      
      return vendorsData;
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
      setVendorList([]);
      setFilteredVendors([]);
      return [];
    }
  };

  // Fetch ALL purchases from expense_items - SORTED DESCENDING (newest first)
  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const url = `https://salathai.co.tz/mrms/Expenses.php?token=${token}&all_items=1`;
      
      const response = await axios.get(url);
      
      if (response.data && response.data.items && Array.isArray(response.data.items)) {
        const items = response.data.items.map(item => ({
          id: item.id,
          expense_id: item.expense_id,
          date: item.expense_date || item.item_created_at,
          product_name: item.product_name,
          category: item.category || item.expense_category,
          vendor_name: item.vendor_name || "Unknown",
          quantity: safeNumber(item.quantity),
          unit: item.unit || "PCS",
          price_per_unit: safeNumber(item.price_per_unit),
          total_amount: safeNumber(item.amount),
          payment_method: item.payment_method,
          receipt: item.receipt,
          recorded_by_name: item.recorded_by || 'System'
        }));
        
        // Sort by date DESCENDING (newest first)
        const sortedItems = items.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Descending: newer dates first
        });
        
        setPurchases(sortedItems);
        setTotalRecords(sortedItems.length);
        
        // Extract unique categories and vendors for filters
        const uniqueCategories = [...new Set(sortedItems.map(i => i.category).filter(c => c))];
        const uniqueVendors = [...new Set(sortedItems.map(i => i.vendor_name).filter(v => v && v !== 'Unknown'))];
        const uniqueProducts = [...new Set(sortedItems.map(i => i.product_name).filter(p => p))];
        
        setCategories(uniqueCategories);
        setVendorsList(uniqueVendors);
        setProductsList(uniqueProducts);
        
        console.log(`Loaded ${sortedItems.length} purchase items from database (newest first)`);
      } else {
        setPurchases([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching purchases:", error);
      setMessage({ text: "Error fetching purchases", type: "error" });
      setPurchases([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Save product
  const saveProduct = async () => {
    if (!productForm.item_name || !productForm.category) {
      setMessage({ text: "Product name and category required", type: "error" });
      return;
    }

    setSavingItem(true);
    try {
      const token = getToken();
      const url = `https://salathai.co.tz/mrms/Products.php?token=${token}&products=1`;
      const method = editingProduct ? "PUT" : "POST";
      
      const data = {
        category: productForm.category,
        item_name: productForm.item_name,
        base_unit: productForm.base_unit,
        purchase_packaging: productForm.purchase_packaging || null,
        packaging_quantity: parseFloat(productForm.packaging_quantity),
        vendor_id: productForm.vendor_id === "" ? null : parseInt(productForm.vendor_id),
        vendor_name: productForm.vendor_name || null
      };
      
      if (editingProduct) {
        data.id = editingProduct.id;
      }
      
      const response = await axios({ method, url, data });
      
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message || "Product saved successfully", type: "success" });
        resetProductForm();
        fetchProducts();
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save product error:", error);
      setMessage({ text: error.response?.data?.error || "Error saving product", type: "error" });
    } finally {
      setSavingItem(false);
    }
  };

  // Save vendor
  const saveVendor = async () => {
    if (!vendorForm.name) {
      setMessage({ text: "Vendor name required", type: "error" });
      return;
    }

    setSavingItem(true);
    try {
      const token = getToken();
      const url = `https://salathai.co.tz/mrms/Vendor.php?token=${token}`;
      const method = editingVendor ? "PUT" : "POST";
      const data = editingVendor ? { ...vendorForm, id: editingVendor.id } : vendorForm;
      
      const response = await axios({ method, url, data });
      
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message || "Vendor saved successfully", type: "success" });
        resetVendorForm();
        fetchVendorsData();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save vendor error:", error);
      setMessage({ text: error.response?.data?.error || "Error saving vendor", type: "error" });
    } finally {
      setSavingItem(false);
    }
  };

  // Delete product
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const token = getToken();
      const response = await axios.delete(`https://salathai.co.tz/mrms/Products.php?id=${id}&token=${token}&products=1`);
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message || "Product deleted successfully", type: "success" });
        fetchProducts();
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error deleting product", type: "error" });
    }
  };

  // Delete vendor
  const deleteVendor = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      const token = getToken();
      const response = await axios.delete(`https://salathai.co.tz/mrms/Vendor.php?id=${id}&token=${token}`);
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message || "Vendor deleted successfully", type: "success" });
        fetchVendorsData();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      setMessage({ text: "Error deleting vendor", type: "error" });
    }
  };

  const editProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      category: product.category || "",
      item_name: product.item_name || "",
      base_unit: product.base_unit || "PCS",
      purchase_packaging: product.purchase_packaging || "",
      packaging_quantity: product.packaging_quantity || 1,
      vendor_id: product.vendor_id || "",
      vendor_name: product.vendor_name || ""
    });
    setShowProductModal(true);
    setShowProductList(false);
  };

  const editVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || "",
      phone: vendor.phone || "",
      payment_methods: vendor.payment_methods || "",
      delivery_type: vendor.delivery_type || "",
      id_number: vendor.id_number || "",
      category: vendor.category || ""
    });
    setShowVendorModal(true);
    setShowVendorList(false);
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.item_name);
    setShowProductDropdown(false);
    
    setFormData({
      ...formData,
      product_id: product.id,
      product_name: product.item_name,
      category: product.category,
      unit: product.base_unit || "PCS",
      quantity: "",
      price_per_unit: "",
      total_amount: ""
    });
    
    if (product.vendor_name) {
      setVendorSearch(product.vendor_name);
      setSelectedVendor({ name: product.vendor_name, id: product.vendor_id });
      setFormData(prev => ({ ...prev, vendor_id: product.vendor_id, vendor_name: product.vendor_name }));
    }
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorSearch(vendor.name);
    setShowVendorDropdown(false);
    setFormData(prev => ({ ...prev, vendor_id: vendor.id, vendor_name: vendor.name }));
  };

  const viewPurchaseDetails = (purchase) => {
    setSelectedPurchaseDetail(purchase);
    setShowViewModal(true);
  };

  const editPurchase = (purchase) => {
    setFormData({
      category: purchase.category || "",
      product_id: "",
      product_name: purchase.product_name || "",
      vendor_id: "",
      vendor_name: purchase.vendor_name || "",
      quantity: purchase.quantity,
      unit: purchase.unit || "PCS",
      price_per_unit: purchase.price_per_unit,
      total_amount: purchase.total_amount
    });
    
    setProductSearch(purchase.product_name || "");
    setVendorSearch(purchase.vendor_name || "");
    
    setEditingId(purchase.id);
    setShowForm(true);
  };

  const deletePurchase = async (id) => {
    if (!window.confirm("Delete this purchase item?")) return;
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.delete(`https://salathai.co.tz/mrms/Expenses.php?item_id=${id}&token=${token}`);
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: "Purchase item deleted successfully", type: "success" });
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Delete error:", error);
      setMessage({ text: "Error deleting purchase", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (e) => {
    const qty = parseFloat(e.target.value) || 0;
    const price = parseFloat(formData.price_per_unit) || 0;
    
    setFormData({
      ...formData,
      quantity: qty,
      total_amount: qty * price
    });
  };

  const handlePriceChange = (e) => {
    const price = parseFloat(e.target.value) || 0;
    const qty = parseFloat(formData.quantity) || 0;
    
    setFormData({
      ...formData,
      price_per_unit: price,
      total_amount: qty * price
    });
  };

  const resetProductForm = () => {
    setProductForm({
      category: "",
      item_name: "",
      base_unit: "PCS",
      purchase_packaging: "",
      packaging_quantity: 1,
      vendor_id: "",
      vendor_name: ""
    });
    setEditingProduct(null);
    setShowProductModal(false);
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: "",
      phone: "",
      payment_methods: "",
      delivery_type: "",
      id_number: "",
      category: ""
    });
    setEditingVendor(null);
    setShowVendorModal(false);
  };

  const resetForm = () => {
    setFormData({
      category: "",
      product_id: "",
      product_name: "",
      vendor_id: "",
      vendor_name: "",
      quantity: "",
      unit: "PCS",
      price_per_unit: "",
      total_amount: ""
    });
    setSelectedProduct(null);
    setSelectedVendor(null);
    setProductSearch("");
    setVendorSearch("");
    setEditingId(null);
    setShowForm(false);
  };

  const savePurchase = async () => {
    if (!formData.product_name || !formData.vendor_id || !formData.quantity || !formData.price_per_unit) {
      setMessage({ text: "Please fill all required fields", type: "error" });
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const amount = safeNumber(formData.quantity) * safeNumber(formData.price_per_unit);
      
      const saveData = {
        category: formData.category,
        product_name: formData.product_name,
        vendor_id: parseInt(formData.vendor_id),
        vendor_name: formData.vendor_name,
        quantity: safeNumber(formData.quantity),
        unit: formData.unit,
        price_per_unit: safeNumber(formData.price_per_unit),
        amount: amount
      };
      
      let response;
      if (editingId) {
        saveData.item_id = editingId;
        response = await axios.put(`https://salathai.co.tz/mrms/Expenses.php?token=${token}`, saveData);
      } else {
        response = await axios.post(`https://salathai.co.tz/mrms/Expenses.php?token=${token}`, saveData);
      }
      
      if (response.data && response.data.error) {
        setMessage({ text: response.data.error, type: "error" });
      } else {
        setMessage({ text: response.data.message || "Purchase saved successfully", type: "success" });
        resetForm();
        fetchPurchases();
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      }
    } catch (error) {
      console.error("Save purchase error:", error);
      setMessage({ text: error.response?.data?.error || "Error saving purchase", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
    fetchProducts();
    fetchVendorsData();
  }, []);

  const handleProductSearch = (e) => {
    const value = e.target.value;
    setProductSearch(value);
    setSelectedProduct(null);
    
    if (value.length > 0) {
      const filtered = products.filter(p => 
        (p.item_name && p.item_name.toLowerCase().includes(value.toLowerCase())) ||
        (p.category && p.category.toLowerCase().includes(value.toLowerCase())) ||
        (p.vendor_name && p.vendor_name.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredProducts(filtered.slice(0, 15));
      setShowProductDropdown(true);
    } else {
      setFilteredProducts([]);
      setShowProductDropdown(false);
    }
  };

  const handleVendorSearch = (e) => {
    const value = e.target.value;
    setVendorSearch(value);
    setSelectedVendor(null);
    
    if (value.length > 0) {
      const filtered = vendors.filter(v => 
        (v.name && v.name.toLowerCase().includes(value.toLowerCase())) ||
        (v.phone && v.phone.includes(value)) ||
        (v.category && v.category.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredVendors(filtered.slice(0, 15));
      setShowVendorDropdown(true);
    } else {
      setFilteredVendors([]);
      setShowVendorDropdown(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target) &&
          productInputRef.current && !productInputRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target) &&
          vendorInputRef.current && !vendorInputRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Apply all filters (data is already sorted descending from fetch)
  const filteredPurchases = purchases.filter(p => {
    let match = true;
    
    if (filterDate) {
      const pDate = new Date(p.date);
      const filterDateObj = new Date(filterDate);
      match = match && pDate.toDateString() === filterDateObj.toDateString();
    }
    
    if (filterCategory && filterCategory !== "ALL") {
      match = match && p.category === filterCategory;
    }
    
    if (filterVendor && filterVendor !== "ALL") {
      match = match && p.vendor_name === filterVendor;
    }
    
    if (filterProduct && filterProduct !== "ALL") {
      match = match && p.product_name === filterProduct;
    }
    
    return match;
  });

  // Pagination calculations - data stays in descending order
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPurchases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterDate, filterCategory, filterVendor, filterProduct, itemsPerPage]);

  // Statistics
  const totalUniqueProducts = new Set(filteredPurchases.map(p => p.product_name)).size;
  const totalVendorsUnique = new Set(filteredPurchases.map(p => p.vendor_name)).size;
  const totalAmount = filteredPurchases.reduce((sum, p) => sum + safeNumber(p.total_amount), 0);
  const avgAmount = filteredPurchases.length > 0 ? totalAmount / filteredPurchases.length : 0;

  // Clear all filters
  const clearFilters = () => {
    setFilterDate("");
    setFilterCategory("");
    setFilterVendor("");
    setFilterProduct("");
  };

  return (
    <div className="pm-container">
      <div className="pm-header">
        <div className="pm-header-left">
          <h1 className="pm-title">Purchase Manager</h1>
          <p className="pm-subtitle">Track all purchased items with unit prices (Newest first)</p>
        </div>
        <div className="pm-header-actions">
          <button onClick={() => setShowReportModal(true)} className="pm-btn pm-btn-outline">
            Export Report
          </button>
          {isAdmin && (
            <>
              <button 
                onClick={() => {
                  setShowProductList(true);
                  fetchProducts();
                }} 
                className="pm-btn pm-btn-outline"
              >
                Manage Products ({productList.length})
              </button>
              <button 
                onClick={() => {
                  setShowVendorList(true);
                  fetchVendorsData();
                }} 
                className="pm-btn pm-btn-outline"
              >
                Manage Vendors ({vendorList.length})
              </button>
            </>
          )}
          <button onClick={() => setShowForm(true)} className="pm-btn pm-btn-primary">
            New Purchase
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`pm-toast pm-toast-${message.type}`}>
          <span className="pm-toast-message">{message.text}</span>
        </div>
      )}

      {/* Filter Bar */}
      <div className="pm-filter-bar">
        <div className="pm-filter-group">
          <label className="pm-filter-label">Date</label>
          <input 
            type="date" 
            className="pm-filter-input" 
            value={filterDate} 
            onChange={(e) => setFilterDate(e.target.value)} 
          />
        </div>
        
        <div className="pm-filter-group">
          <label className="pm-filter-label">Category</label>
          <select 
            className="pm-filter-select" 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="ALL">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        
        <div className="pm-filter-group">
          <label className="pm-filter-label">Vendor</label>
          <select 
            className="pm-filter-select" 
            value={filterVendor} 
            onChange={(e) => setFilterVendor(e.target.value)}
          >
            <option value="ALL">All Vendors</option>
            {vendorsList.map(vend => (
              <option key={vend} value={vend}>{vend}</option>
            ))}
          </select>
        </div>
        
        <div className="pm-filter-group">
          <label className="pm-filter-label">Product</label>
          <select 
            className="pm-filter-select" 
            value={filterProduct} 
            onChange={(e) => setFilterProduct(e.target.value)}
          >
            <option value="ALL">All Products</option>
            {productsList.map(prod => (
              <option key={prod} value={prod}>{prod}</option>
            ))}
          </select>
        </div>
        
        <button onClick={clearFilters} className="pm-btn pm-btn-clear">
          Clear Filters
        </button>
        
        <div className="pm-filter-stats">
          Records: {filteredPurchases.length} / {totalRecords}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="pm-stats">
        <div className="pm-stat-card">
          <div className="pm-stat-label">Total Purchases</div>
          <div className="pm-stat-value">{filteredPurchases.length}</div>
        </div>
        <div className="pm-stat-card">
          <div className="pm-stat-label">Total Amount</div>
          <div className="pm-stat-value">TZS {formatTZS(totalAmount)}</div>
        </div>
        <div className="pm-stat-card">
          <div className="pm-stat-label">Unique Vendors</div>
          <div className="pm-stat-value">{totalVendorsUnique}</div>
        </div>
        <div className="pm-stat-card">
          <div className="pm-stat-label">Avg Purchase</div>
          <div className="pm-stat-value">TZS {formatTZS(avgAmount)}</div>
        </div>
        <div className="pm-stat-card">
          <div className="pm-stat-label">Unique Products</div>
          <div className="pm-stat-value">{totalUniqueProducts}</div>
        </div>
      </div>

      {/* Items Per Page Selector */}
      <div className="pm-per-page">
        <label>Show:</label>
        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span>entries per page (Newest first)</span>
      </div>

      {/* Table */}
      <div className="pm-table-wrapper">
        {loading ? (
          <div className="pm-state pm-state-loading">Loading purchases...</div>
        ) : filteredPurchases.length === 0 ? (
          <div className="pm-state pm-state-empty">No purchases found. Click "New Purchase" to add one.</div>
        ) : (
          <div className="pm-table-responsive">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Product Name</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Price per Unit (TZS)</th>
                  <th>Total Amount (TZS)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((p, idx) => (
                  <tr key={p.id}>
                    <td>{indexOfFirstItem + idx + 1}</td>
                    <td>{p.date ? new Date(p.date).toLocaleDateString('en-GB') : '-'}</td>
                    <td className="pm-table-product">
                      <strong>{p.product_name || '-'}</strong>
                    </td>
                    <td>
                      <span className="category-badge">{p.category || '-'}</span>
                    </td>
                    <td>
                      <span className="vendor-name">{p.vendor_name || "Unknown"}</span>
                    </td>
                    <td className="pm-table-quantity">
                      <strong>{formatTZS(p.quantity)}</strong>
                    </td>
                    <td>{p.unit || 'PCS'}</td>
                    <td className="pm-table-price">
                      TZS {formatTZS(p.price_per_unit)}
                    </td>
                    <td className="pm-table-total">
                      TZS {formatTZS(p.total_amount)}
                    </td>
                    <td className="pm-table-actions">
                      <button onClick={() => viewPurchaseDetails(p)} className="pm-table-btn pm-table-view">View</button>
                      <button onClick={() => editPurchase(p)} className="pm-table-btn pm-table-edit">Edit</button>
                      <button onClick={() => deletePurchase(p.id)} className="pm-table-btn pm-table-delete">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="pm-table-footer">
                  <td colSpan="8" className="pm-footer-label"><strong>Grand Total:</strong></td>
                  <td colSpan="2" className="pm-footer-total">
                    <strong>TZS {formatTZS(totalAmount)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Pagination - Fully functional to navigate to oldest records */}
      {totalPages > 1 && (
        <div className="pm-pagination">
          <button 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1}
            className="pm-pagination-btn"
          >
            ⏮ First
          </button>
          <button 
            onClick={() => setCurrentPage(p => p - 1)} 
            disabled={currentPage === 1}
            className="pm-pagination-btn"
          >
            ◀ Previous
          </button>
          <span className="pm-pagination-info">
            Page {currentPage} of {totalPages} | 
            Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredPurchases.length)} of {filteredPurchases.length} records
            {currentPage === 1 && " (Newest)"}
            {currentPage === totalPages && " (Oldest)"}
          </span>
          <button 
            onClick={() => setCurrentPage(p => p + 1)} 
            disabled={currentPage === totalPages}
            className="pm-pagination-btn"
          >
            Next ▶
          </button>
          <button 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages}
            className="pm-pagination-btn"
          >
            Last ⏭
          </button>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="pm-modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">Export Purchases Report</h2>
              <button onClick={() => setShowReportModal(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-form">
              <p className="pm-hint">
                Total records: {totalRecords} | Current filtered: {filteredPurchases.length}
              </p>
              
              <div className="pm-form-group">
                <label className="pm-label">Report Format</label>
                <select 
                  className="pm-select" 
                  value={exportFormat} 
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <option value="detailed">Detailed Report (All purchases)</option>
                  <option value="summary">Summary Report (Group by Product)</option>
                </select>
              </div>
              
              <div className="pm-report-summary">
                <div className="pm-summary-card">
                  <span className="pm-summary-label">Total Purchases:</span>
                  <span className="pm-summary-value">{filteredPurchases.length}</span>
                </div>
                <div className="pm-summary-card">
                  <span className="pm-summary-label">Total Amount:</span>
                  <span className="pm-summary-value">TZS {formatTZS(totalAmount)}</span>
                </div>
              </div>
            </div>
            
            <div className="pm-modal-footer">
              <button onClick={() => setShowReportModal(false)} className="pm-btn pm-btn-secondary">Cancel</button>
              <button onClick={exportPurchasesReport} disabled={isExporting} className="pm-btn pm-btn-primary">
                {isExporting ? 'Generating...' : 'Export Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedPurchaseDetail && (
        <div className="pm-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="pm-modal pm-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">Purchase Details</h2>
              <button onClick={() => setShowViewModal(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-view-details">
              <div className="pm-detail-section">
                <h3>Purchase Information</h3>
                <div className="pm-detail-grid">
                  <div className="pm-detail-item">
                    <label>Date:</label>
                    <span>{selectedPurchaseDetail.date ? new Date(selectedPurchaseDetail.date).toLocaleString() : '-'}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Product:</label>
                    <span>{selectedPurchaseDetail.product_name || '-'}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Category:</label>
                    <span>{selectedPurchaseDetail.category || '-'}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Vendor:</label>
                    <span>{selectedPurchaseDetail.vendor_name || "Unknown"}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Payment Method:</label>
                    <span>{selectedPurchaseDetail.payment_method || "N/A"}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Receipt:</label>
                    <span>{selectedPurchaseDetail.receipt || "N/A"}</span>
                  </div>
                  <div className="pm-detail-item">
                    <label>Recorded By:</label>
                    <span>{selectedPurchaseDetail.recorded_by_name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="pm-detail-section">
                <h3>Unit Breakdown</h3>
                <div className="pm-unit-breakdown">
                  <div className="pm-breakdown-card">
                    <div className="pm-breakdown-label">Quantity Purchased:</div>
                    <div className="pm-breakdown-value">{formatTZS(selectedPurchaseDetail.quantity)}</div>
                    <div className="pm-breakdown-unit">{selectedPurchaseDetail.unit || 'PCS'}</div>
                  </div>
                  <div className="pm-breakdown-card">
                    <div className="pm-breakdown-label">Price per Unit:</div>
                    <div className="pm-breakdown-value">TZS {formatTZS(selectedPurchaseDetail.price_per_unit)}</div>
                    <div className="pm-breakdown-unit">per {selectedPurchaseDetail.unit || 'unit'}</div>
                  </div>
                  <div className="pm-breakdown-card pm-breakdown-total">
                    <div className="pm-breakdown-label">Total Amount:</div>
                    <div className="pm-breakdown-value">TZS {formatTZS(selectedPurchaseDetail.total_amount)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pm-modal-footer">
              <button onClick={() => setShowViewModal(false)} className="pm-btn pm-btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Form Modal */}
      {showForm && (
        <div className="pm-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="pm-modal pm-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">{editingId ? "Edit Purchase" : "New Purchase"}</h2>
              <button onClick={() => setShowForm(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-form">
              <div className="pm-form-group">
                <label className="pm-label">Product *</label>
                <div className="pm-search-wrapper">
                  <input
                    ref={productInputRef}
                    type="text"
                    className="pm-input"
                    placeholder="Search product..."
                    value={productSearch}
                    onChange={handleProductSearch}
                    autoComplete="off"
                  />
                  {showProductDropdown && (
                    <div className="pm-dropdown" ref={productDropdownRef}>
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map(p => (
                          <div key={p.id} className="pm-dropdown-item" onClick={() => selectProduct(p)}>
                            <div className="pm-dropdown-item-title">{p.item_name}</div>
                            <div className="pm-dropdown-item-meta">
                              <span className="pm-badge pm-badge-sm">{p.category}</span>
                              <span className="pm-badge pm-badge-sm">{p.base_unit}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="pm-dropdown-empty">No products found.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="pm-form-group">
                <label className="pm-label">Vendor *</label>
                <div className="pm-search-wrapper">
                  <input
                    ref={vendorInputRef}
                    type="text"
                    className="pm-input"
                    placeholder="Search vendor..."
                    value={vendorSearch}
                    onChange={handleVendorSearch}
                    autoComplete="off"
                  />
                  {showVendorDropdown && (
                    <div className="pm-dropdown" ref={vendorDropdownRef}>
                      {filteredVendors.length > 0 ? (
                        filteredVendors.map(v => (
                          <div key={v.id} className="pm-dropdown-item" onClick={() => selectVendor(v)}>
                            <div className="pm-dropdown-item-title">{v.name}</div>
                            <div className="pm-dropdown-item-meta">
                              {v.phone && <span className="pm-badge pm-badge-sm">{v.phone}</span>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="pm-dropdown-empty">No vendors found.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selectedProduct && (
                <div className="pm-info-card">
                  <div className="pm-info-card-title">Product Details</div>
                  <div className="pm-info-grid">
                    <div className="pm-info-item"><span className="pm-info-label">Category:</span> {selectedProduct.category}</div>
                    <div className="pm-info-item"><span className="pm-info-label">Unit:</span> {selectedProduct.base_unit}</div>
                    {selectedProduct.vendor_name && (
                      <div className="pm-info-item"><span className="pm-info-label">Default Vendor:</span> {selectedProduct.vendor_name}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label className="pm-label">Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="pm-input"
                    placeholder="Enter quantity"
                    value={formData.quantity}
                    onChange={handleQuantityChange}
                  />
                </div>
                <div className="pm-form-group">
                  <label className="pm-label">Unit</label>
                  <select 
                    className="pm-select"
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                  >
                    <option value="PCS">PCS (Pieces)</option>
                    <option value="KG">KG (Kilograms)</option>
                    <option value="L">L (Liters)</option>
                    <option value="ML">ML (Milliliters)</option>
                    <option value="GR">GR (Grams)</option>
                  </select>
                </div>
              </div>

              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label className="pm-label">Price per Unit (TZS) *</label>
                  <input
                    type="number"
                    step="1"
                    className="pm-input"
                    placeholder="0"
                    value={formData.price_per_unit}
                    onChange={handlePriceChange}
                  />
                </div>
                <div className="pm-form-group">
                  <label className="pm-label">Total Amount (TZS)</label>
                  <input
                    type="text"
                    className="pm-input pm-input-highlight"
                    value={formatTZS(formData.total_amount)}
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="pm-modal-footer">
              <button onClick={resetForm} className="pm-btn pm-btn-secondary">Cancel</button>
              <button onClick={savePurchase} disabled={loading} className="pm-btn pm-btn-primary">
                {loading ? "Saving..." : editingId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT LIST MODAL - Full CRUD */}
      {showProductList && (
        <div className="pm-modal-overlay" onClick={() => setShowProductList(false)}>
          <div className="pm-modal pm-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">Manage Products</h2>
              <button onClick={() => setShowProductList(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-list-actions">
              <button 
                onClick={() => {
                  resetProductForm();
                  setEditingProduct(null);
                  setShowProductModal(true);
                }} 
                className="pm-btn pm-btn-primary"
              >
                + Add New Product
              </button>
              <div className="pm-list-count">Total Products: {productList.length}</div>
            </div>
            
            <div className="pm-table-responsive">
              <table className="pm-table pm-table-compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>Category</th>
                    <th>Base Unit</th>
                    <th>Packaging</th>
                    <th>Vendor</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="pm-empty-state">No products found. Click "Add New Product" to create one.</td>
                    </tr>
                  ) : (
                    productList.map((product, idx) => (
                      <tr key={product.id}>
                        <td>{idx + 1}</td>
                        <td><strong>{product.item_name}</strong></td>
                        <td><span className="category-badge">{product.category || '-'}</span></td>
                        <td>{product.base_unit || 'PCS'}</td>
                        <td>{product.purchase_packaging ? `${product.packaging_quantity || 1} x ${product.purchase_packaging}` : '-'}</td>
                        <td>{product.vendor_name || '-'}</td>
                        <td className="pm-table-actions">
                          <button onClick={() => editProduct(product)} className="pm-table-btn pm-table-edit">Edit</button>
                          <button onClick={() => deleteProduct(product.id)} className="pm-table-btn pm-table-delete">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="pm-modal-footer">
              <button onClick={() => setShowProductList(false)} className="pm-btn pm-btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCT FORM MODAL (Create/Edit) */}
      {showProductModal && (
        <div className="pm-modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">{editingProduct ? "Edit Product" : "Add New Product"}</h2>
              <button onClick={() => setShowProductModal(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-form">
              <div className="pm-form-group">
                <label className="pm-label">Product Name *</label>
                <input
                  type="text"
                  className="pm-input"
                  value={productForm.item_name}
                  onChange={(e) => setProductForm({...productForm, item_name: e.target.value})}
                  placeholder="Enter product name"
                />
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Category *</label>
                <select
                  className="pm-select"
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                >
                  <option value="">Select Category</option>
                  {productCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label className="pm-label">Base Unit</label>
                  <select
                    className="pm-select"
                    value={productForm.base_unit}
                    onChange={(e) => setProductForm({...productForm, base_unit: e.target.value})}
                  >
                    <option value="PCS">PCS (Pieces)</option>
                    <option value="KG">KG (Kilograms)</option>
                    <option value="L">L (Liters)</option>
                    <option value="ML">ML (Milliliters)</option>
                    <option value="GR">GR (Grams)</option>
                  </select>
                </div>
                <div className="pm-form-group">
                  <label className="pm-label">Packaging Quantity</label>
                  <input
                    type="number"
                    step="1"
                    className="pm-input"
                    value={productForm.packaging_quantity}
                    onChange={(e) => setProductForm({...productForm, packaging_quantity: parseInt(e.target.value) || 1})}
                  />
                </div>
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Purchase Packaging</label>
                <input
                  type="text"
                  className="pm-input"
                  value={productForm.purchase_packaging}
                  onChange={(e) => setProductForm({...productForm, purchase_packaging: e.target.value})}
                  placeholder="e.g., Carton, Box, Crate"
                />
              </div>
              
              <div className="pm-form-row">
                <div className="pm-form-group">
                  <label className="pm-label">Default Vendor</label>
                  <select
                    className="pm-select"
                    value={productForm.vendor_id}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      const vendor = vendors.find(v => v.id === parseInt(vendorId));
                      setProductForm({
                        ...productForm,
                        vendor_id: vendorId,
                        vendor_name: vendor ? vendor.name : ""
                      });
                    }}
                  >
                    <option value="">Select Vendor (Optional)</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div className="pm-modal-footer">
              <button onClick={() => setShowProductModal(false)} className="pm-btn pm-btn-secondary">Cancel</button>
              <button onClick={saveProduct} disabled={savingItem} className="pm-btn pm-btn-primary">
                {savingItem ? "Saving..." : editingProduct ? "Update Product" : "Save Product"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VENDOR LIST MODAL - Full CRUD */}
      {showVendorList && (
        <div className="pm-modal-overlay" onClick={() => setShowVendorList(false)}>
          <div className="pm-modal pm-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">Manage Vendors</h2>
              <button onClick={() => setShowVendorList(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-list-actions">
              <button 
                onClick={() => {
                  resetVendorForm();
                  setEditingVendor(null);
                  setShowVendorModal(true);
                }} 
                className="pm-btn pm-btn-primary"
              >
                + Add New Vendor
              </button>
              <div className="pm-list-count">Total Vendors: {vendorList.length}</div>
            </div>
            
            <div className="pm-table-responsive">
              <table className="pm-table pm-table-compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vendor Name</th>
                    <th>Phone</th>
                    <th>Category</th>
                    <th>Payment Methods</th>
                    <th>Delivery Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorList.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="pm-empty-state">No vendors found. Click "Add New Vendor" to create one.</td>
                    </tr>
                  ) : (
                    vendorList.map((vendor, idx) => (
                      <tr key={vendor.id}>
                        <td>{idx + 1}</td>
                        <td><strong>{vendor.name}</strong></td>
                        <td>{vendor.phone || '-'}</td>
                        <td>{vendor.category || '-'}</td>
                        <td>{vendor.payment_methods || '-'}</td>
                        <td>{vendor.delivery_type || '-'}</td>
                        <td className="pm-table-actions">
                          <button onClick={() => editVendor(vendor)} className="pm-table-btn pm-table-edit">Edit</button>
                          <button onClick={() => deleteVendor(vendor.id)} className="pm-table-btn pm-table-delete">Delete</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="pm-modal-footer">
              <button onClick={() => setShowVendorList(false)} className="pm-btn pm-btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* VENDOR FORM MODAL (Create/Edit) */}
      {showVendorModal && (
        <div className="pm-modal-overlay" onClick={() => setShowVendorModal(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2 className="pm-modal-title">{editingVendor ? "Edit Vendor" : "Add New Vendor"}</h2>
              <button onClick={() => setShowVendorModal(false)} className="pm-modal-close">×</button>
            </div>
            
            <div className="pm-form">
              <div className="pm-form-group">
                <label className="pm-label">Vendor Name *</label>
                <input
                  type="text"
                  className="pm-input"
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                  placeholder="Enter vendor name"
                />
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Phone Number</label>
                <input
                  type="text"
                  className="pm-input"
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                  placeholder="Enter phone number"
                />
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Category</label>
                <select
                  className="pm-select"
                  value={vendorForm.category}
                  onChange={(e) => setVendorForm({...vendorForm, category: e.target.value})}
                >
                  <option value="">Select Category</option>
                  {vendorCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Payment Methods</label>
                <input
                  type="text"
                  className="pm-input"
                  value={vendorForm.payment_methods}
                  onChange={(e) => setVendorForm({...vendorForm, payment_methods: e.target.value})}
                  placeholder="e.g., Cash, Bank Transfer, M-Pesa"
                />
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">Delivery Type</label>
                <select
                  className="pm-select"
                  value={vendorForm.delivery_type}
                  onChange={(e) => setVendorForm({...vendorForm, delivery_type: e.target.value})}
                >
                  <option value="">Select Delivery Type</option>
                  <option value="pickup">Pickup Only</option>
                  <option value="delivery">Delivery Only</option>
                  <option value="both">Both</option>
                </select>
              </div>
              
              <div className="pm-form-group">
                <label className="pm-label">ID/TIN Number</label>
                <input
                  type="text"
                  className="pm-input"
                  value={vendorForm.id_number}
                  onChange={(e) => setVendorForm({...vendorForm, id_number: e.target.value})}
                  placeholder="Enter ID or TIN number"
                />
              </div>
            </div>
            
            <div className="pm-modal-footer">
              <button onClick={() => setShowVendorModal(false)} className="pm-btn pm-btn-secondary">Cancel</button>
              <button onClick={saveVendor} disabled={savingItem} className="pm-btn pm-btn-primary">
                {savingItem ? "Saving..." : editingVendor ? "Update Vendor" : "Save Vendor"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Purchase;